import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IncomeExpenses } from '../components/insights/IncomeExpensesLinearChart';
import { NavigationProps } from '../components/insights/Navigation';
import { InsightsWidgetHeader, ProfileInsightsStates } from '../components/insights/InsightsWidgetHeader';
import { InsightsWidgetFooter } from '../components/insights/InsightsWidgetFooter';
import { DashboardContext } from '../contexts/DashboardContext';
import { DiffBarChart, IDiffBarChartItem } from '../components/insights/DiffBarChart';
import { useStore } from '../../../store';
import { ProfitInsightsWidgetSkeleton } from '../components/skeletons/ProfitInsightsWidgetSkeleton';
import { getStartTimeForPeriodType } from '../utils/insights.utils';
import { fetchNewInsightsByType, InsightType } from '../queryFunctions/fetchInsightsByType';
import { trackMixPanel } from '../../../services/mixpanel.service';
import bugsnagHandler from '../../../services/bugsnag.service';

type TotalItem = {
  title: string;
  total: number;
};

function convertArrayToObject(totalItems: TotalItem[]): Record<string, TotalItem> {
  return totalItems.reduce(
    (obj, item) => {
      obj[item.title] = item;
      return obj;
    },
    {} as Record<string, TotalItem>,
  );
}

export enum ChartType {
  LineDiff,
  BarChartDiff,
}

interface ProfitInsightsProps {
  isLoading: boolean;
}

interface IProfitSegmentChartItem {
  total: number;
  valueType: string;
}

interface IChartItem {
  profitSegmentChartItems: IProfitSegmentChartItem[];
  title: string;
}

export const testIdPrefix = 'ProfitInsightsWidget';

export const ProfitInsights = ({ isLoading }: ProfitInsightsProps) => {
  const { t } = useTranslation('dashboardFaceliftResourcesEN');
  const { profitChartData, chartFilters } = useContext(DashboardContext);
  const { customerId } = useStore((state) => state.currUser());
  const [periodType, setPeriodType] = useState<ProfileInsightsStates>(ProfileInsightsStates.Yearly);
  const [subPeriodType, setSubPeriodType] = useState<string>((new Date()).getFullYear().toString());
  const [chartType, setChartType] = useState<ChartType>(ChartType.LineDiff);
  const [shouldUseInitialData, setShouldUseInitialData] = useState<boolean>(true);
  const [isLoadingNewData, setIsLoadingNewData] = useState<boolean>(false);
  const [newFetchedData, setNewFetchedData] = useState<any>();
  const profitInsightsMainData = shouldUseInitialData ? profitChartData : newFetchedData?.data;
  const yearlyLinerChart = periodType === ProfileInsightsStates.Yearly && chartType === ChartType.LineDiff;
  const yearlyBarChart = periodType === ProfileInsightsStates.Yearly && chartType === ChartType.BarChartDiff;
  const monthlyLinerChart = periodType === ProfileInsightsStates.Monthly;
  const quarterlyLinerChart = periodType === ProfileInsightsStates.Quarterly && chartType === ChartType.LineDiff;
  const quarterlyQuarterChart = periodType === ProfileInsightsStates.Quarterly && chartType === ChartType.BarChartDiff;
  const shouldShowChartIndex =
    yearlyBarChart || quarterlyQuarterChart || (monthlyLinerChart && chartType !== ChartType.LineDiff);
  const totalItems = useMemo(() => {
    if (!profitInsightsMainData?.totalItems) return {};
    return convertArrayToObject(profitInsightsMainData.totalItems as TotalItem[]);
  }, [profitInsightsMainData?.totalItems]);

  // Navigation options defaults
  const [navOptions, setNavOptions] = useState<NavigationProps>({
    isNext: false,
    isPrevious: false,
    onNext: () => false,
    onPrevious: () => false,
  });

  useEffect(() => {
    if (chartType === ChartType.LineDiff) {
      trackMixPanel('Act on Profit Before Tax Widget', { WidgetView: 'Summary View' });
    }
    if (chartType === ChartType.BarChartDiff) {
      trackMixPanel('Act on Profit Before Tax Widget', { WidgetView: 'Timeline View' });
    }
  }, [chartType]);

  useEffect(() => {
    if (periodType === ProfileInsightsStates.Yearly && chartType === ChartType.LineDiff) {
      setNavOptions({
        isNext: true,
        isPrevious: false,
        onNext: () => {
          setChartType(ChartType.BarChartDiff);
          trackMixPanel('Act on Profit Before Tax Widget', { Action: 'Arrow Right' });
        },
        onPrevious: () => false,
      });
    }

    if (periodType === ProfileInsightsStates.Yearly && chartType === ChartType.BarChartDiff) {
      setNavOptions({
        isNext: false,
        isPrevious: true,
        onNext: () => false,
        onPrevious: () => {
          setChartType(ChartType.LineDiff);
          trackMixPanel('Act on Profit Before Tax Widget', { Action: 'Arrow Left' });
        },
      });
    }

    if (periodType === ProfileInsightsStates.Monthly) {
      setNavOptions({
        isNext: false,
        isPrevious: false,
        onNext: () => false,
        onPrevious: () => false,
      });
    }

    if (periodType === ProfileInsightsStates.Quarterly && chartType === ChartType.LineDiff) {
      setNavOptions({
        isNext: true,
        isPrevious: false,
        onNext: () => {
          setChartType(ChartType.BarChartDiff);
        },
        onPrevious: () => false,
      });
    }

    if (periodType === ProfileInsightsStates.Quarterly && chartType === ChartType.BarChartDiff) {
      setNavOptions({
        isNext: false,
        isPrevious: true,
        onNext: () => false,
        onPrevious: () => {
          setChartType(ChartType.LineDiff);
        },
      });
    }
  }, [periodType, chartType]);
  const handleFilterChange = (selectedPeriodType: ProfileInsightsStates, selectedSubPeriodType: string) => {
    const startTime = getStartTimeForPeriodType(chartFilters, selectedPeriodType, selectedSubPeriodType);
    if (startTime) {
      setIsLoadingNewData(true);
      fetchNewInsightsByType(customerId, selectedPeriodType, startTime, InsightType.Profits)
        .then((data: any) => {
          if (shouldUseInitialData) setShouldUseInitialData(false);
          setPeriodType(selectedPeriodType);
          setSubPeriodType(selectedSubPeriodType);
          setNewFetchedData(data);
        })
        .catch((e) => {
          bugsnagHandler.notify(
            `FetchInsightsByType error message: ${e?.message} and props: customerId: ${customerId} , periodType: ${selectedPeriodType}, startTime: ${startTime}, insightType: ${InsightType.Profits}`,
          );
        })
        .finally(() => {
          setIsLoadingNewData(false);
        });
    }
    trackMixPanel('Act on Profit Before Tax Widget', { Action: selectedPeriodType });
    trackMixPanel('Act on Profit Before Tax Widget', { Action: selectedSubPeriodType });
  };

  const mapChartItems = (chartItems: IChartItem[]) =>
    chartItems?.map((chartItem: IChartItem) => {
      const { profitSegmentChartItems } = chartItem || {};
      const moreChartItemProps = profitSegmentChartItems.reduce(
        (acc: object, obj: IProfitSegmentChartItem) => ({
          ...acc,
          [obj.valueType.toLowerCase()]: obj.total,
        }),
        {},
      );
      return {
        month: chartItem.title,
        ...moreChartItemProps,
      };
    });
  if (isLoading || isLoadingNewData) return <ProfitInsightsWidgetSkeleton />;
  return (
    <div
      className='flex flex-col bg-bw00 rounded-[12px] p-[19px] pb-[13px] h-full justify-between'
      data-testid={`${testIdPrefix}_main-wrapper`}
    >
      <InsightsWidgetHeader
        title={t('INSIGHT_WIDGET.PROFIT_TITLE')}
        chartSummaryData={profitInsightsMainData?.chartSummaryData}
        onStateChange={handleFilterChange}
        showChartIndex={shouldShowChartIndex}
        chartFilters={chartFilters}
        periodType={periodType}
        subPeriodType={subPeriodType}
        isMonthlyPeriodDisabled={chartType === ChartType.BarChartDiff}
      />
      <div className='pt-[10px]'>
        {yearlyLinerChart && <IncomeExpenses income={totalItems?.Income} expense={totalItems?.Expense} />}

        {yearlyBarChart && (
          <DiffBarChart
            chartItems={mapChartItems(profitInsightsMainData?.profitChartItems) as IDiffBarChartItem[]}
            chartType={chartType}
          />
        )}

        {monthlyLinerChart && <IncomeExpenses income={totalItems?.Income} expense={totalItems?.Expense} />}

        {quarterlyLinerChart && <IncomeExpenses income={totalItems?.Income} expense={totalItems?.Expense} />}

        {quarterlyQuarterChart && (
          <DiffBarChart
            chartItems={mapChartItems(profitInsightsMainData?.profitChartItems) as IDiffBarChartItem[]}
            chartType={chartType}
          />
        )}
        <InsightsWidgetFooter {...navOptions} />
      </div>
    </div>
  );
};
